import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import {
    TableMap,
    tableNodeTypes,
    cellAround,
    pointsAtCell,
    setAttr,
    nextCell,
} from "prosemirror-tables";
import { TableView, updateColumns } from "./table-view";

export const key = new PluginKey("tableColumnResizing");

const MAX_TABLE_WIDTH = 650;

// Helper function to get the actual available width for a table
function getAvailableTableWidth(view, tableStart) {
    // tableStart is the position before the table node
    const $tableStart = view.state.doc.resolve(tableStart);

    // Walk up the tree to find if we're inside a table cell
    for (let depth = $tableStart.depth; depth >= 0; depth--) {
        const node = $tableStart.node(depth);

        // Check if this node is a table cell or header
        if (node && (node.type.name === "table_cell" || node.type.name === "table_header")) {
            try {
                // Get the position of this cell
                const cellStart = $tableStart.start(depth);

                // Try to get the DOM element for this cell
                const domInfo = view.domAtPos(cellStart);
                let cellDom = domInfo.node;

                // Walk up the DOM tree to find the TD or TH element
                while (cellDom && cellDom.nodeName !== "TD" && cellDom.nodeName !== "TH") {
                    cellDom = cellDom.parentNode;
                }

                if (cellDom) {
                    // Get the actual width of the parent cell
                    const parentCellWidth = cellDom.offsetWidth;

                    // Account for padding and borders (adjust these values based on your CSS)
                    const padding = 20; // Typical padding for table cells
                    const availableWidth = Math.max(100, parentCellWidth - padding);
                    return availableWidth;
                }

                // Fallback: try to get width from cell's colwidth attribute
                if (node.attrs && node.attrs.colwidth && node.attrs.colwidth[0]) {
                    const colWidth = node.attrs.colwidth[0];
                    return Math.max(100, colWidth - 20); // Subtract padding
                }
            } catch (e) {
                console.warn("Error getting parent cell width:", e);
            }
        }
    }

    // Check one more way: if the table itself is inside a cell
    try {
        // Get the table DOM element
        const tableDom = view.domAtPos(tableStart + 1).node;
        if (tableDom) {
            let parentCell = tableDom.closest("td, th");
            if (parentCell) {
                const parentCellWidth = parentCell.offsetWidth;
                const padding = 20;
                return Math.max(100, parentCellWidth - padding);
            }
        }
    } catch (e) {
        console.warn("Error in DOM traversal:", e);
    }

    // If not nested, return the default max width
    return MAX_TABLE_WIDTH;
}

// Helper function to get current table width
function getCurrentTableWidth(view, tableStart) {
    try {
        // tableStart is the position before the table
        const $pos = view.state.doc.resolve(tableStart);
        const table = $pos.nodeAfter;

        if (!table || table.type.spec.tableRole !== "table") {
            return MAX_TABLE_WIDTH;
        }

        // Try to get the DOM node for the table
        const tableDom = view.domAtPos(tableStart + 1).node;
        if (tableDom) {
            let tableElement = tableDom;

            // Find the actual TABLE element
            while (tableElement && tableElement.nodeName !== "TABLE") {
                if (tableElement.nodeName === "TABLE") {
                    break;
                }
                tableElement = tableElement.parentNode;
            }

            if (tableElement && tableElement.nodeName === "TABLE") {
                const width = tableElement.offsetWidth;
                return width;
            }
        }

        // Fallback: calculate from column widths
        const map = TableMap.get(table);
        let totalWidth = 0;

        // Get the width of each column from the first row
        for (let col = 0; col < map.width; col++) {
            let cellPos = map.map[col];
            let cell = table.nodeAt(cellPos);
            if (cell && cell.attrs.colwidth && cell.attrs.colwidth[0]) {
                totalWidth += cell.attrs.colwidth[0];
            } else {
                // Try to get width from DOM
                try {
                    const cellDom = view.domAtPos(tableStart + 1 + cellPos).node;
                    if (cellDom && cellDom.offsetWidth) {
                        totalWidth += cellDom.offsetWidth;
                    } else {
                        totalWidth += 100; // Default fallback
                    }
                } catch (e) {
                    totalWidth += 100; // Default fallback
                }
            }
        }

        return totalWidth || MAX_TABLE_WIDTH;
    } catch (e) {
        return MAX_TABLE_WIDTH;
    }
}

export function columnResizing({
    handleWidth = 5,
    cellMinWidth = 25,
    View = TableView,
    tables,
    editor,
    lastColumnResizable = true,
    maxTableWidth = MAX_TABLE_WIDTH, // This will be overridden for nested tables
    isEditable = true,
} = {}) {
    let plugin = new Plugin({
        key,
        state: {
            init(arg, state) {
                this.spec.props.nodeViews[tableNodeTypes(state.schema).table.name] = (
                    node,
                    view,
                    getPos,
                ) =>
                    new View({
                        editor,
                        node,
                        view,
                        getPos,
                        isEditable,
                        tables,
                    });

                return new ResizeState(-1, false);
            },
            apply(tr, prev) {
                return prev.apply(tr);
            },
        },
        props: {
            attributes(state) {
                let pluginState = key.getState(state);
                const editablePlugin = state.plugins.find((plugin) =>
                    plugin.key.startsWith("editable$"),
                );
                const editable = editablePlugin.props.editable();
                if (!editable) {
                    return null;
                }
                return pluginState.activeHandle > -1 ? { class: "resize-cursor" } : null;
            },

            handleDOMEvents: isEditable
                ? {
                      mousemove(view, event) {
                          handleMouseMove(
                              view,
                              event,
                              handleWidth,
                              cellMinWidth,
                              lastColumnResizable,
                          );
                      },
                      mouseleave(view) {
                          handleMouseLeave(view);
                      },
                      mousedown(view, event) {
                          handleMouseDown(view, event, cellMinWidth, handleWidth, maxTableWidth);
                      },
                  }
                : {},

            decorations(state) {
                let pluginState = key.getState(state);
                if (pluginState.activeHandle > -1) {
                    return handleDecorations(state, pluginState.activeHandle);
                }
            },

            nodeViews: {},
        },
    });
    return plugin;
}

class ResizeState {
    constructor(activeHandle, dragging, columnWidthsInitialized) {
        this.activeHandle = activeHandle;
        this.dragging = dragging;
        this.columnWidthsInitialized = columnWidthsInitialized;
    }

    apply(tr) {
        let state = this,
            action = tr.getMeta(key);
        if (action && action.columnWidthsInitialized) {
            return new ResizeState(state.activeHandle, state.dragging, true);
        }
        // having an !== causes a crash
        // eslint-disable-next-line eqeqeq
        if (action && action.setHandle != null) {
            return new ResizeState(action.setHandle, null);
        }
        if (action && action.setDragging !== undefined) {
            return new ResizeState(state.activeHandle, action.setDragging);
        }
        if (state.activeHandle > -1 && tr.docChanged) {
            let handle = tr.mapping.map(state.activeHandle, -1);
            if (!pointsAtCell(tr.doc.resolve(handle))) {
                handle = null;
            }
            state = new ResizeState(handle, state.dragging);
        }
        return state;
    }
}

function handleMouseMove(view, event, handleWidth, cellMinWidth, lastColumnResizable) {
    let pluginState = key.getState(view.state);

    if (!pluginState.dragging) {
        let target = domCellAround(event.target),
            cell = -1;
        if (target) {
            let { left, right } = target.getBoundingClientRect();
            if (event.clientX - left <= handleWidth) {
                cell = edgeCell(view, event, "left");
            } else if (right - event.clientX <= handleWidth) {
                cell = edgeCell(view, event, "right");
            }
        }

        if (cell !== pluginState.activeHandle) {
            if (!lastColumnResizable && cell !== -1) {
                let $cell = view.state.doc.resolve(cell);
                let table = $cell.node(-1),
                    map = TableMap.get(table),
                    start = $cell.start(-1);
                let col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;

                if (col === map.width - 1) {
                    return;
                }
            }

            updateHandle(view, cell);
        }
    }
}

function handleMouseLeave(view) {
    let pluginState = key.getState(view.state);
    if (pluginState.activeHandle > -1 && !pluginState.dragging) {
        updateHandle(view, -1);
    }
}

function setInitialColumnWidths(view, activeHandle, effectiveMaxWidth) {
    let pluginState = key.getState(view.state);

    if (pluginState.columnWidthsInitialized) {
        return;
    }

    const $cell = view.state.doc.resolve(activeHandle);
    const start = $cell.start(-1);
    const table = $cell.node(-1);
    const map = TableMap.get(table);
    const colCount = map.width;

    // Use the effective max width instead of hardcoded value
    const initialColWidth = parseInt(effectiveMaxWidth / colCount, 10);

    try {
        map.map.forEach((pos) => {
            const cell = table.nodeAt(pos);
            const { attrs } = cell;
            const { colwidth, colspan } = attrs;

            if (!colwidth) {
                view.dispatch(
                    view.state.tr.setNodeMarkup(
                        start + pos,
                        null,
                        setAttr(attrs, "colwidth", new Array(colspan).fill(initialColWidth)),
                    ),
                );
            }
        });

        view.dispatch(view.state.tr.setMeta(key, { columnWidthsInitialized: true }));
    } catch (ex) {
        // no op
    }
}

function handleMouseDown(view, event, cellMinWidth, handleWidth, maxTableWidth) {
    let pluginState = key.getState(view.state);
    const editablePlugin = view.state.plugins.find((plugin) => plugin.key.startsWith("editable$"));
    const editable = editablePlugin.props.editable();
    if (!editable || pluginState.activeHandle === -1 || pluginState.dragging) {
        return false;
    }

    let cell = view.state.doc.nodeAt(pluginState.activeHandle);

    // Get the table position
    const $cell = view.state.doc.resolve(pluginState.activeHandle);
    const tableStart = $cell.start(-1);

    // Calculate effective max width for this specific table
    const availableWidth = getAvailableTableWidth(view, tableStart);
    const currentTableWidth = getCurrentTableWidth(view, tableStart);

    // For nested tables, the effective max should be the available width from parent
    // For non-nested tables, use the smaller of current width and max width
    let effectiveMaxWidth;
    if (availableWidth < MAX_TABLE_WIDTH) {
        // This indicates we found a parent cell constraint
        effectiveMaxWidth = availableWidth;
    } else {
        // Not nested or couldn't detect parent - use normal constraints
        effectiveMaxWidth = Math.min(currentTableWidth, maxTableWidth);
    }

    setInitialColumnWidths(view, pluginState.activeHandle, effectiveMaxWidth);

    cell = view.state.doc.nodeAt(pluginState.activeHandle);
    let width = currentColWidth(view, pluginState.activeHandle, cell.attrs);

    view.dispatch(
        view.state.tr.setMeta(key, {
            setDragging: {
                startX: event.clientX,
                startWidth: width,
                effectiveMaxWidth: effectiveMaxWidth, // Store the effective max width
            },
        }),
    );

    updateColumnWidth(view, pluginState.activeHandle, width);

    function finish(event, resetWidth) {
        window.removeEventListener("mouseup", finish);
        window.removeEventListener("mousemove", move);
        let pluginState = key.getState(view.state);
        if (pluginState.dragging) {
            let dragged = resetWidth || draggedWidth(pluginState.dragging, event, cellMinWidth);

            // Ensure dragged width doesn't exceed available space
            const maxAllowedWidth = pluginState.dragging.effectiveMaxWidth || effectiveMaxWidth;
            dragged = Math.min(dragged, maxAllowedWidth);

            updateColumnWidth(view, pluginState.activeHandle, dragged);

            let nextTableCell = nextCell(
                view.state.tr.doc.resolve(pluginState.activeHandle),
                "horiz",
                1,
            );
            if (nextTableCell) {
                let nextCellNode = view.state.tr.doc.nodeAt(nextTableCell.pos);
                let nextCellColwidth = currentColWidth(view, nextTableCell.pos, nextCellNode.attrs);
                let startWidth = pluginState.dragging.startWidth;
                let updatedColVal = nextCellColwidth;

                if (startWidth >= dragged) {
                    updatedColVal += startWidth - dragged;
                } else {
                    updatedColVal -= dragged - startWidth;
                }

                updateColumnWidth(view, nextTableCell.pos, Math.max(cellMinWidth, updatedColVal));
            }
            view.dispatch(view.state.tr.setMeta(key, { setDragging: null }));
        }
    }

    function getCellWidth(activeHandle) {
        if (!width) {
            let $cell = view.state.doc.resolve(activeHandle);
            let table = $cell.node(-1);
            const columnCount = table.childCount;

            return parseInt(effectiveMaxWidth / columnCount);
        }

        return width;
    }

    function handleLastTableCellResize({ event, pluginState, dragged, currentCellWidth }) {
        // Get the effective max width from the dragging state
        const maxAllowed = pluginState.dragging.effectiveMaxWidth || effectiveMaxWidth;

        // Calculate the current total width of all columns
        const $cell = view.state.doc.resolve(pluginState.activeHandle);
        const table = $cell.node(-1);

        // Verify this is actually a table node
        if (!table || table.type.spec.tableRole !== "table") {
            return finish(event, currentCellWidth);
        }

        const map = TableMap.get(table);
        let totalOtherColsWidth = 0;

        for (let col = 0; col < map.width - 1; col++) {
            let cellPos = map.map[col];
            let cell = table.nodeAt(cellPos);
            if (cell && cell.attrs.colwidth && cell.attrs.colwidth[0]) {
                totalOtherColsWidth += cell.attrs.colwidth[0];
            }
        }

        // Don't allow the last column to push the table beyond its container
        const maxLastColWidth = maxAllowed - totalOtherColsWidth;

        if (dragged > maxLastColWidth) {
            return finish(event, maxLastColWidth);
        }

        if (dragged > currentCellWidth + handleWidth) {
            return finish(event, currentCellWidth);
        }

        displayColumnWidth(view, pluginState.activeHandle, dragged, cellMinWidth);
    }

    function handleTableCellResize({
        event,
        pluginState,
        nextTableCell,
        dragged,
        currentCellWidth,
    }) {
        const nextCellNode = view.state.tr.doc.nodeAt(nextTableCell.pos);
        const nextCellColWidth = currentColWidth(view, nextTableCell.pos, nextCellNode.attrs);

        const maxResizeWidth = currentCellWidth + nextCellColWidth - cellMinWidth;

        // Also check against the effective max width
        const maxAllowed = pluginState.dragging.effectiveMaxWidth || effectiveMaxWidth;
        const effectiveMaxResize = Math.min(maxResizeWidth, maxAllowed);

        // handle is used as a margin to prevent weird behavior when starting to drag
        if (dragged > effectiveMaxResize + handleWidth && pluginState.dragging) {
            // we have reached the end of available resize
            return finish(event, effectiveMaxResize);
        }

        displayColumnWidth(view, pluginState.activeHandle, dragged, cellMinWidth);
    }

    function move(event) {
        if (!event.which) {
            return finish(event);
        }

        const pluginState = key.getState(view.state);
        const dragged = draggedWidth(pluginState.dragging, event, cellMinWidth);
        const currentCellWidth = getCellWidth(pluginState.activeHandle);

        let nextTableCell = nextCell(
            view.state.tr.doc.resolve(pluginState.activeHandle),
            "horiz",
            1,
        );

        if (!nextTableCell || !nextTableCell?.pos) {
            handleLastTableCellResize({ event, pluginState, dragged, currentCellWidth });
            return;
        }

        handleTableCellResize({ event, pluginState, nextTableCell, dragged, currentCellWidth });
    }

    window.addEventListener("mouseup", finish);
    window.addEventListener("mousemove", move);
    event.preventDefault();
    return true;
}

function currentColWidth(view, cellPos, { colspan, colwidth }) {
    let width = colwidth && colwidth[colwidth.length - 1];
    if (width) {
        return width;
    }
    let dom = view.domAtPos(cellPos);
    let node = dom.node.childNodes[dom.offset];
    let domWidth = node.offsetWidth,
        parts = colspan;
    if (colwidth) {
        for (let i = 0; i < colspan; i++) {
            if (colwidth[i]) {
                domWidth -= colwidth[i];
                parts--;
            }
        }
    }
    return domWidth / parts;
}

function domCellAround(target) {
    while (target && target.nodeName !== "TD" && target.nodeName !== "TH") {
        target = target.classList.contains("ProseMirror") ? null : target.parentNode;
    }
    return target;
}

function edgeCell(view, event, side) {
    let found = view.posAtCoords({ left: event.clientX, top: event.clientY });
    if (!found) {
        return -1;
    }
    let { pos } = found;
    let $cell = cellAround(view.state.doc.resolve(pos));
    if (!$cell) {
        return -1;
    }
    if (side === "right") {
        return $cell.pos;
    }
    let map = TableMap.get($cell.node(-1)),
        start = $cell.start(-1);
    let index = map.map.indexOf($cell.pos - start);
    return index % map.width === 0 ? -1 : start + map.map[index - 1];
}

function draggedWidth(dragging, event, cellMinWidth) {
    let offset = event.clientX - dragging.startX;
    return Math.max(cellMinWidth, dragging.startWidth + offset);
}

function updateHandle(view, value) {
    view.dispatch(view.state.tr.setMeta(key, { setHandle: value }));
}

function updateColumnWidth(view, cell, width) {
    let $cell = view.state.doc.resolve(cell);
    let table = $cell.node(-1),
        map = TableMap.get(table),
        start = $cell.start(-1);
    let col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;
    let tr = view.state.tr;

    // Track the table we're working with to avoid affecting nested tables
    const targetTableStart = start;
    const targetTableEnd = start + table.nodeSize;

    for (let row = 0; row < map.height; row++) {
        let mapIndex = row * map.width + col;
        // Rowspanning cell that has already been handled
        if (row && map.map[mapIndex] === map.map[mapIndex - map.width]) {
            continue;
        }
        let pos = map.map[mapIndex],
            { attrs } = table.nodeAt(pos);
        let index = attrs.colspan === 1 ? 0 : col - map.colCount(pos);
        if (attrs.colwidth && attrs.colwidth[index] === width) {
            continue;
        }
        let colwidth = attrs.colwidth ? attrs.colwidth.slice() : zeroes(attrs.colspan);
        colwidth[index] = width;

        const absolutePos = start + pos;

        // Ensure we're updating a cell within our target table, not a nested table
        if (absolutePos >= targetTableStart && absolutePos < targetTableEnd) {
            tr.setNodeMarkup(absolutePos, null, setAttr(attrs, "colwidth", colwidth));
        }
    }
    if (tr.docChanged) {
        view.dispatch(tr);
    }
}

function displayColumnWidth(view, cell, width, cellMinWidth) {
    let $cell = view.state.doc.resolve(cell);
    let table = $cell.node(-1),
        start = $cell.start(-1);
    let col = TableMap.get(table).colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;
    let dom = view.domAtPos($cell.start(-1)).node;
    while (dom.nodeName !== "TABLE") {
        dom = dom.parentNode;
    }
    updateColumns(table, dom.firstChild, dom, cellMinWidth, col, width);
}

function zeroes(n) {
    let result = [];
    for (let i = 0; i < n; i++) {
        result.push(0);
    }
    return result;
}

function handleDecorations(state, cell) {
    let decorations = [];
    let $cell = state.doc.resolve(cell);

    // Safely find the table by traversing up the document tree
    let table = null;
    let tableDepth = null;
    for (let depth = $cell.depth; depth >= 0; depth--) {
        const node = $cell.node(depth);
        if (node?.type?.spec?.tableRole === "table") {
            table = node;
            tableDepth = depth;
            break;
        }
    }

    if (!table || tableDepth === null) {
        return DecorationSet.empty;
    }

    let map = TableMap.get(table);
    let start = $cell.start(tableDepth);
    let col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan;
    for (let row = 0; row < map.height; row++) {
        let index = col + row * map.width - 1;
        // For positions that are have either a different cell or the end
        // of the table to their right, and either the top of the table or
        // a different cell above them, add a decoration
        if (
            (col === map.width || map.map[index] !== map.map[index + 1]) &&
            (row === 0 || map.map[index - 1] !== map.map[index - 1 - map.width])
        ) {
            let cellPos = map.map[index];
            let pos = start + cellPos + table.nodeAt(cellPos).nodeSize - 1;
            let dom = document.createElement("div");
            dom.className = "column-resize-handle";
            decorations.push(Decoration.widget(pos, dom));
        }
    }
    return DecorationSet.create(state.doc, decorations);
}
